import React from 'react';
import BottomSheet from '../../../Shared/BottomSheet';
import { WarningBadge, WarningType } from '../Warning/WarningBadge';
import type { WarningReason } from '../../../../utils/computeWarning';

interface WarningBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  type: WarningType;
  imageUrl?: string;
  title?: string;
  reasons: WarningReason[];
}

const WarningBottomSheet: React.FC<WarningBottomSheetProps> = ({
  isOpen,
  onClose,
  type,
  imageUrl,
  title,
  reasons,
}) => {
  const hasIngredients = reasons.some((r) => r.type === 'ingredient');
  const hasTags = reasons.some((r) => r.type === 'tag');
  const hasAllergy = reasons.some((r) => r.warningType === 'allergy');
  const hasAvoid = reasons.some((r) => r.warningType === 'avoid');

  const heading =
    hasAllergy && hasAvoid
      ? 'Allergy & Preference Alert'
      : hasAllergy
        ? 'Allergy Alert'
        : 'Preference Alert';

  let subtext = '';
  if (hasIngredients && hasTags) {
    if (hasAllergy && hasAvoid) {
      subtext =
        'This recipe contains ingredients and tags you are allergic to and chose to avoid:';
    } else if (hasAllergy) {
      subtext =
        'This recipe contains ingredients and tags you are allergic to:';
    } else {
      subtext = 'This recipe contains ingredients and tags you chose to avoid:';
    }
  } else if (hasIngredients) {
    subtext = hasAllergy
      ? 'This recipe contains ingredients you are allergic to:'
      : 'This recipe contains ingredients you chose to avoid:';
  } else {
    subtext = hasAllergy
      ? 'This recipe contains tags you are allergic to:'
      : 'This recipe contains tags you chose to avoid:';
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <h2 className="flex items-center justify-center gap-2 mb-4 text-lg font-semibold">
        <WarningBadge type={type} />
        {heading}
      </h2>

      {imageUrl && (
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-40 object-cover rounded-lg mb-3"
        />
      )}

      {title && <h3 className="text-center font-medium mb-2">{title}</h3>}
      <p className="text-center text-sm mb-3">{subtext}</p>

      <ul className="list-disc list-inside text-sm mb-6 space-y-1">
        {reasons.map((r) => (
          <li key={`${r.name}-${r.type}-${r.warningType}`}>
            {r.type === 'tag' && r.category
              ? `${capitalize(r.category)}: ${r.name}`
              : r.warningType === 'allergy'
                ? `Allergy: ${r.name}`
                : `Avoid: ${r.name}`}
          </li>
        ))}
      </ul>
    </BottomSheet>
  );
};

function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default WarningBottomSheet;
